import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, Paper, Grid, Alert, CircularProgress,
  List, ListItem, ListItemText, Divider, Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  RestartAlt as RestartIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import useStore from '../config/store';
import CaptationChatWindow from './CaptationChatWindow';

const RESULT_COLORS = {
  captado: 'success',
  registrado_base: 'info',
  cerrado: 'default',
};

/**
 * Simulador del bot de captación (Flujo 1 de reclutamiento masivo).
 * Modo sandbox: el backend marca la conversación como isTest — no crea
 * applicants reales ni consume servicios externos.
 * Ruta: /ats/positions/:positionId/simulator
 */
const CaptationSimulator = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { positionId } = useParams();
  const { authToken } = useStore.getState();

  const [conversationId, setConversationId] = useState(null);
  const [positionName, setPositionName] = useState('');
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(true);
  const [error, setError] = useState(null);

  // Estado del motor (panel de debug)
  const [engineState, setEngineState] = useState('interes');
  const [lastIntent, setLastIntent] = useState(null);
  const [result, setResult] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(null);
  const [turnTokens, setTurnTokens] = useState(null);
  const [totalTokens, setTotalTokens] = useState(0);
  const [expectsDocument, setExpectsDocument] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${authToken}` } };

  const startSimulation = useCallback(async () => {
    try {
      setStarting(true);
      setError(null);
      setMessages([]);
      setAnswers([]);
      setResult(null);
      setLastIntent(null);
      setQuestionIndex(0);
      setTurnTokens(null);
      setTotalTokens(0);
      setExpectsDocument(false);

      const response = await api.post(
        `/captation/${positionId}/simulator/start`,
        {},
        authHeaders
      );
      const data = response.data.response;

      if (data?.status_response === 'OK') {
        setConversationId(data.conversationId);
        setPositionName(data.positionName || '');
        setEngineState(data.state);
        setTotalQuestions(data.totalQuestions || null);
        setExpectsDocument(Boolean(data.expectsDocument));
        setMessages([{ role: 'bot', content: data.botMessage }]);
      } else {
        setError(t('ats.captation.simulator.startError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('ats.captation.simulator.startError'));
    } finally {
      setStarting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId]);

  useEffect(() => {
    if (positionId) startSimulation();
  }, [positionId, startSimulation]);

  const sendTurn = async (bubble, body) => {
    setMessages((prev) => [...prev, { role: 'candidate', content: bubble }]);
    setSending(true);
    try {
      const response = await api.post(
        `/captation/${positionId}/simulator/${conversationId}/message`,
        body,
        authHeaders
      );
      const data = response.data.response;

      if (data?.status_response === 'OK') {
        setMessages((prev) => [...prev, { role: 'bot', content: data.botMessage }]);
        setEngineState(data.state);
        setExpectsDocument(Boolean(data.expectsDocument));
        setResult(data.result || null);
        setAnswers(data.answers || []);
        setLastIntent(data.debug?.intent || null);
        if (typeof data.currentQuestionIndex === 'number') setQuestionIndex(data.currentQuestionIndex);
        setTurnTokens(data.llmUsage ? data.llmUsage.total_tokens : null);
        if (data.llmUsage?.total_tokens) {
          setTotalTokens((prev) => prev + data.llmUsage.total_tokens);
        }
      }
    } catch (err) {
      console.error(err);
      setMessages((prev) => [...prev, { role: 'bot', content: t('ats.captation.public.error') }]);
    } finally {
      setSending(false);
    }
  };

  const handleSend = (text) => sendTurn(text, { content: text });

  // Adjunto SIMULADO: no se sube nada — solo viaja el nombre del archivo y el
  // motor avanza igual que en producción (sandbox sin servicios externos)
  const handleSendFile = (file) =>
    sendTurn(`📎 ${file.name}`, { simulateAttachment: { fileName: file.name } });

  const finished = engineState === 'finalizada';

  if (starting) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/ats/positions/${positionId}`)}
        color="inherit"
        size="small"
        sx={{ mb: 1, ml: -1 }}
      >
        {t('ats.captation.simulator.backToPosition')}
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="h5" fontWeight={700}>
          {t('ats.captation.simulator.title')}
        </Typography>
        <Chip
          icon={<ScienceIcon />}
          label={t('ats.captation.simulator.testChip')}
          color="warning"
          size="small"
          variant="outlined"
        />
        {positionName && <Chip label={positionName} size="small" />}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ats.captation.simulator.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={startSimulation}>
            {t('ats.captation.public.retry')}
          </Button>
        }>
          {error}
        </Alert>
      )}

      {!error && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <CaptationChatWindow
              messages={messages}
              onSend={handleSend}
              expectsDocument={expectsDocument}
              onSendFile={handleSendFile}
              sending={sending}
              finished={finished}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2">
                  {t('ats.captation.simulator.panelTitle')}
                </Typography>
                <Tooltip title={t('ats.captation.simulator.restart')}>
                  <Button size="small" startIcon={<RestartIcon />} onClick={startSimulation}>
                    {t('ats.captation.simulator.restart')}
                  </Button>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('ats.captation.simulator.state')}
                  </Typography>
                  <Chip
                    label={t(`ats.captation.states.${engineState}`, engineState)}
                    size="small"
                    color={finished ? 'default' : 'primary'}
                    variant="outlined"
                  />
                </Box>

                {result && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('ats.captation.simulator.result')}
                    </Typography>
                    <Chip
                      label={t(`ats.captation.results.${result}`, result)}
                      size="small"
                      color={RESULT_COLORS[result] || 'default'}
                    />
                  </Box>
                )}

                {lastIntent && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('ats.captation.simulator.intent')}
                    </Typography>
                    <Typography variant="body2">{lastIntent}</Typography>
                  </Box>
                )}

                {engineState === 'preguntas' && totalQuestions && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('ats.captation.simulator.progress')}
                    </Typography>
                    <Typography variant="body2">{Math.min(questionIndex + 1, totalQuestions)} / {totalQuestions}</Typography>
                  </Box>
                )}

                {turnTokens !== null && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('ats.captation.simulator.turnTokens')}
                    </Typography>
                    <Typography variant="body2">{turnTokens}</Typography>
                  </Box>
                )}

                {totalTokens > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('ats.captation.simulator.totalTokens')}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>{totalTokens.toLocaleString('es-CL')}</Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('ats.captation.simulator.answers')}
              </Typography>

              {answers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('ats.captation.simulator.noAnswers')}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {answers.map((a) => (
                    <ListItem key={a.key} disableGutters sx={{ py: 0.25 }}>
                      <ListItemText
                        primary={a.answer}
                        // Preguntas personalizadas de la vacante (fq_*): mostrar el
                        // texto de la pregunta en vez de la clave interna
                        secondary={a.key.startsWith('fq_') ? (a.questionText || a.key) : a.key}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption', noWrap: true, title: a.questionText || a.key }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default CaptationSimulator;
